import React, { useEffect, useState } from 'react';
import { Layout, Menu, Button, Modal, Form, Input, Space, Spin, message } from 'antd';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PictureOutlined, FolderOutlined, AppstoreOutlined, FileImageOutlined, FontColorsOutlined, LogoutOutlined, LockOutlined, HighlightOutlined } from '@ant-design/icons';
import { adminAuth, clearAdminAuth, getAdminToken, getAdminUsername } from '../api';

const { Header, Sider, Content } = Layout;

const menuItems = [
  { key: '/dindoor', icon: <PictureOutlined />, label: <Link to="/dindoor">海报管理</Link> },
  { key: '/dindoor/categories', icon: <FolderOutlined />, label: <Link to="/dindoor/categories">分类管理</Link> },
  { key: '/dindoor/image-library', icon: <FileImageOutlined />, label: <Link to="/dindoor/image-library">图片库</Link> },
  { key: '/dindoor/background-images', icon: <FileImageOutlined />, label: <Link to="/dindoor/background-images">背景图</Link> },
  { key: '/dindoor/material-items', icon: <AppstoreOutlined />, label: <Link to="/dindoor/material-items">素材库</Link> },
  { key: '/dindoor/builtin-assets', icon: <AppstoreOutlined />, label: <Link to="/dindoor/builtin-assets">内置素材</Link> },
  { key: '/dindoor/font-assets', icon: <FontColorsOutlined />, label: <Link to="/dindoor/font-assets">字体管理</Link> },
  { key: '/dindoor/text-styles', icon: <HighlightOutlined />, label: <Link to="/dindoor/text-styles">文字素材</Link> }
];

function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [authChecking, setAuthChecking] = useState(true);
  const [username, setUsername] = useState(getAdminUsername());
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    const token = getAdminToken();

    if (!token) {
      navigate('/dindoor/login', { replace: true });
      return;
    }

    adminAuth.me()
      .then((response) => {
        setUsername(response.data.data.username);
      })
      .catch(() => {
        clearAdminAuth();
        navigate('/dindoor/login', { replace: true });
      })
      .finally(() => {
        setAuthChecking(false);
      });
  }, [location.pathname, navigate]);

  const handleLogout = async () => {
    try {
      await adminAuth.logout();
    } catch (err) {
      // ignore
    } finally {
      clearAdminAuth();
      navigate('/dindoor/login', { replace: true });
    }
  };

  const handleChangePassword = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await adminAuth.changePassword(values);
      message.success('密码修改成功');
      setPasswordModalVisible(false);
      form.resetFields();
    } catch (err) {
      if (err?.errorFields) {
        return;
      }

      message.error(err.response?.data?.message || '修改密码失败');
    } finally {
      setSubmitting(false);
    }
  };

  if (authChecking) {
    return <Spin size="large" style={{ display: 'block', margin: '120px auto' }} />;
  }

  return (
    <>
      <Layout style={{ minHeight: '100vh' }}>
        <Sider theme="dark">
          <div style={{ padding: '16px', textAlign: 'center', color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>
            海报管理后台
          </div>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
          />
        </Sider>
        <Layout>
          <Header style={{ background: '#fff', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 style={{ margin: 0 }}>瑞华海报管理系统</h2>
            <Space>
              <span style={{ color: '#667085' }}>管理员：{username || 'admin'}</span>
              <Button type="link" href="/" target="_blank" rel="noreferrer">
                前台预览
              </Button>
              <Button icon={<LockOutlined />} onClick={() => setPasswordModalVisible(true)}>
                修改密码
              </Button>
              <Button icon={<LogoutOutlined />} onClick={handleLogout}>
                退出登录
              </Button>
            </Space>
          </Header>
          <Content style={{ margin: '24px 16px', padding: 24, background: '#fff' }}>
            <Outlet />
          </Content>
        </Layout>
      </Layout>

      <Modal
        title="修改管理员密码"
        open={passwordModalVisible}
        onOk={handleChangePassword}
        onCancel={() => {
          setPasswordModalVisible(false);
          form.resetFields();
        }}
        confirmLoading={submitting}
        maskClosable={false}
        keyboard={false}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="oldPassword"
            label="旧密码"
            rules={[{ required: true, message: '请输入旧密码' }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '新密码至少 6 位' }
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default AdminLayout;
